/**
 * Enterprise + classic Ext-inspired UI theme presets for AppCenter Next (Vuetify 3).
 */

export type EnterpriseThemeId =
  | 'material'
  | 'muiLight'
  | 'muiDark'
  | 'triton'
  | 'neptune'
  | 'crisp'
  | 'classic'
  | 'gray'
  | 'aria'
  | 'fluent'
  | 'lightning'
  | 'fiori'
  | 'carbon'
  | 'slate'
  | 'night'

export interface EnterpriseThemeMeta {
  id: EnterpriseThemeId
  label: string
  description: string
  swatch: string
  dark: boolean
  /** Group for theme picker */
  group: 'material' | 'ext' | 'enterprise' | 'dark'
}

export const THEME_CATALOG: EnterpriseThemeMeta[] = [
  // Material / MUI
  {
    id: 'material',
    label: 'Material',
    description: 'Google Material Design 3 — indigo primary, clean surfaces',
    swatch: '#6750A4',
    dark: false,
    group: 'material',
  },
  {
    id: 'muiLight',
    label: 'MUI Light',
    description: 'Material UI default blue — modern React/MUI apps',
    swatch: '#1976D2',
    dark: false,
    group: 'material',
  },
  {
    id: 'muiDark',
    label: 'MUI Dark',
    description: 'Material UI dark mode — elevated surfaces',
    swatch: '#90CAF9',
    dark: true,
    group: 'material',
  },
  // Ext JS classics
  {
    id: 'triton',
    label: 'Triton',
    description: 'Ext JS Triton — modern flat blue (classic AppCenter era)',
    swatch: '#5FA2DD',
    dark: false,
    group: 'ext',
  },
  {
    id: 'neptune',
    label: 'Neptune',
    description: 'Ext JS Neptune — light blue-gray enterprise skin',
    swatch: '#3892D3',
    dark: false,
    group: 'ext',
  },
  {
    id: 'crisp',
    label: 'Crisp',
    description: 'Ext JS Crisp — green accent, airy white panels',
    swatch: '#6EAF5F',
    dark: false,
    group: 'ext',
  },
  {
    id: 'classic',
    label: 'Classic',
    description: 'Ext JS Classic Blue — traditional desktop ERP look',
    swatch: '#157FCC',
    dark: false,
    group: 'ext',
  },
  {
    id: 'gray',
    label: 'Gray',
    description: 'Ext JS Gray — neutral silver chrome for dense grids',
    swatch: '#6B6B6B',
    dark: false,
    group: 'ext',
  },
  {
    id: 'aria',
    label: 'Aria',
    description: 'Ext JS Aria — high-contrast accessibility palette',
    swatch: '#F9A825',
    dark: true,
    group: 'ext',
  },
  // Enterprise product skins
  {
    id: 'fluent',
    label: 'Fluent',
    description: 'Microsoft Fluent — Office / Azure blue',
    swatch: '#0078D4',
    dark: false,
    group: 'enterprise',
  },
  {
    id: 'lightning',
    label: 'Lightning',
    description: 'Salesforce Lightning — CRM blue & navy',
    swatch: '#0176D3',
    dark: false,
    group: 'enterprise',
  },
  {
    id: 'fiori',
    label: 'Fiori',
    description: 'SAP Fiori — enterprise blue on cool gray',
    swatch: '#0A6ED1',
    dark: false,
    group: 'enterprise',
  },
  {
    id: 'carbon',
    label: 'Carbon',
    description: 'IBM Carbon — high-contrast product blue',
    swatch: '#0F62FE',
    dark: false,
    group: 'enterprise',
  },
  {
    id: 'slate',
    label: 'Slate Pro',
    description: 'Neutral charcoal enterprise chrome',
    swatch: '#334155',
    dark: false,
    group: 'enterprise',
  },
  {
    id: 'night',
    label: 'Night Ops',
    description: 'Dark operations console for dense grids',
    swatch: '#90CAF9',
    dark: true,
    group: 'dark',
  },
]

/** Vuetify theme color maps */
export const VUETIFY_THEMES: Record<
  EnterpriseThemeId,
  { dark: boolean; colors: Record<string, string> }
> = {
  material: {
    dark: false,
    colors: {
      primary: '#6750A4',
      secondary: '#625B71',
      accent: '#7D5260',
      error: '#B3261E',
      info: '#4A90A4',
      success: '#386A20',
      warning: '#7D5700',
      background: '#FFFBFE',
      surface: '#FFFBFE',
      'on-primary': '#FFFFFF',
      'on-secondary': '#FFFFFF',
      'on-surface': '#1C1B1F',
    },
  },
  muiLight: {
    dark: false,
    colors: {
      primary: '#1976D2',
      secondary: '#9C27B0',
      accent: '#82B1FF',
      error: '#D32F2F',
      info: '#0288D1',
      success: '#2E7D32',
      warning: '#ED6C02',
      background: '#F5F5F5',
      surface: '#FFFFFF',
      'on-primary': '#FFFFFF',
      'on-secondary': '#FFFFFF',
      'on-surface': '#212121',
    },
  },
  muiDark: {
    dark: true,
    colors: {
      primary: '#90CAF9',
      secondary: '#CE93D8',
      accent: '#80CBC4',
      error: '#EF9A9A',
      info: '#81D4FA',
      success: '#A5D6A7',
      warning: '#FFE082',
      background: '#121212',
      surface: '#1E1E1E',
      'on-primary': '#0D47A1',
      'on-secondary': '#4A148C',
      'on-surface': '#E0E0E0',
    },
  },
  triton: {
    dark: false,
    colors: {
      primary: '#5FA2DD',
      secondary: '#404040',
      accent: '#7BB8E8',
      error: '#CF4C35',
      info: '#5FA2DD',
      success: '#7AB86C',
      warning: '#E2A864',
      background: '#F6F6F6',
      surface: '#FFFFFF',
      'on-primary': '#FFFFFF',
      'on-secondary': '#FFFFFF',
      'on-surface': '#404040',
    },
  },
  neptune: {
    dark: false,
    colors: {
      primary: '#3892D3',
      secondary: '#91AEBF',
      accent: '#157FCC',
      error: '#CF4C35',
      info: '#3892D3',
      success: '#53A93F',
      warning: '#F4B04F',
      background: '#DFEAF2',
      surface: '#FFFFFF',
      'on-primary': '#FFFFFF',
      'on-secondary': '#FFFFFF',
      'on-surface': '#293F4E',
    },
  },
  crisp: {
    dark: false,
    colors: {
      primary: '#6EAF5F',
      secondary: '#4B4B4B',
      accent: '#8BC34A',
      error: '#C23B22',
      info: '#5B9BD5',
      success: '#6EAF5F',
      warning: '#F0AD4E',
      background: '#F5F5F5',
      surface: '#FFFFFF',
      'on-primary': '#FFFFFF',
      'on-secondary': '#FFFFFF',
      'on-surface': '#333333',
    },
  },
  classic: {
    dark: false,
    colors: {
      primary: '#157FCC',
      secondary: '#6B6B6B',
      accent: '#D0E4F5',
      error: '#C30',
      info: '#157FCC',
      success: '#090',
      warning: '#F90',
      background: '#DFE8F6',
      surface: '#FFFFFF',
      'on-primary': '#FFFFFF',
      'on-secondary': '#FFFFFF',
      'on-surface': '#000000',
    },
  },
  gray: {
    dark: false,
    colors: {
      primary: '#6B6B6B',
      secondary: '#919191',
      accent: '#A3A3A3',
      error: '#C30',
      info: '#5B9BD5',
      success: '#7AB86C',
      warning: '#E2A864',
      background: '#F0F0F0',
      surface: '#FFFFFF',
      'on-primary': '#FFFFFF',
      'on-secondary': '#FFFFFF',
      'on-surface': '#333333',
    },
  },
  aria: {
    dark: true,
    colors: {
      primary: '#F9A825',
      secondary: '#90CAF9',
      accent: '#FFEE58',
      error: '#EF5350',
      info: '#42A5F5',
      success: '#66BB6A',
      warning: '#FFA726',
      background: '#000000',
      surface: '#121212',
      'on-primary': '#000000',
      'on-secondary': '#000000',
      'on-surface': '#FFFFFF',
    },
  },
  fluent: {
    dark: false,
    colors: {
      primary: '#0078D4',
      secondary: '#8764B8',
      accent: '#50E6FF',
      error: '#D13438',
      info: '#0078D4',
      success: '#107C10',
      warning: '#FFB900',
      background: '#F3F2F1',
      surface: '#FFFFFF',
      'on-primary': '#FFFFFF',
      'on-secondary': '#FFFFFF',
      'on-surface': '#201F1E',
    },
  },
  lightning: {
    dark: false,
    colors: {
      primary: '#0176D3',
      secondary: '#1B96FF',
      accent: '#014486',
      error: '#EA001E',
      info: '#0B5CAB',
      success: '#2E844A',
      warning: '#DD7A01',
      background: '#F3F3F3',
      surface: '#FFFFFF',
      'on-primary': '#FFFFFF',
      'on-secondary': '#FFFFFF',
      'on-surface': '#181818',
    },
  },
  fiori: {
    dark: false,
    colors: {
      primary: '#0A6ED1',
      secondary: '#6A6D70',
      accent: '#0854A0',
      error: '#BB0000',
      info: '#0A6ED1',
      success: '#107E3E',
      warning: '#E9730C',
      background: '#F5F6F7',
      surface: '#FFFFFF',
      'on-primary': '#FFFFFF',
      'on-secondary': '#FFFFFF',
      'on-surface': '#32363A',
    },
  },
  carbon: {
    dark: false,
    colors: {
      primary: '#0F62FE',
      secondary: '#8A3FFC',
      accent: '#0043CE',
      error: '#DA1E28',
      info: '#0043CE',
      success: '#198038',
      warning: '#F1C21B',
      background: '#F4F4F4',
      surface: '#FFFFFF',
      'on-primary': '#FFFFFF',
      'on-secondary': '#FFFFFF',
      'on-surface': '#161616',
    },
  },
  slate: {
    dark: false,
    colors: {
      primary: '#334155',
      secondary: '#0F766E',
      accent: '#64748B',
      error: '#B91C1C',
      info: '#0369A1',
      success: '#15803D',
      warning: '#C2410C',
      background: '#F1F5F9',
      surface: '#FFFFFF',
      'on-primary': '#FFFFFF',
      'on-secondary': '#FFFFFF',
      'on-surface': '#0F172A',
    },
  },
  night: {
    dark: true,
    colors: {
      primary: '#90CAF9',
      secondary: '#CE93D8',
      accent: '#80CBC4',
      error: '#EF9A9A',
      info: '#81D4FA',
      success: '#A5D6A7',
      warning: '#FFE082',
      background: '#121212',
      surface: '#1E1E1E',
      'on-primary': '#0D47A1',
      'on-secondary': '#4A148C',
      'on-surface': '#E0E0E0',
    },
  },
}

/** Shell / AG Grid CSS variables (row striping uses odd/even) */
export function themeCssVars(id: EnterpriseThemeId): Record<string, string> {
  const t = VUETIFY_THEMES[id]
  const p = t.colors.primary
  const bg = t.colors.background
  const surface = t.colors.surface
  const on = t.colors['on-surface']

  if (t.dark) {
    return {
      '--ac-north-bg': '#1a1a1a',
      '--ac-north-fg': '#E0E0E0',
      '--ac-west-head-bg': '#252525',
      '--ac-west-head-fg': '#B0BEC5',
      '--ac-title-accent': p,
      '--ac-border': 'rgba(255,255,255,0.14)',
      '--ac-shell-bg': bg,
      '--ag-header-background-color': '#2a2a2a',
      '--ag-background-color': surface,
      '--ag-foreground-color': on,
      '--ag-border-color': 'rgba(255,255,255,0.18)',
      '--ag-secondary-border-color': 'rgba(255,255,255,0.12)',
      '--ag-odd-row-background-color': '#262626',
      '--ag-even-row-background-color': surface,
      '--ag-selected-row-background-color': hexToRgba(p, 0.22),
      '--ag-row-hover-color': hexToRgba(p, 0.1),
    }
  }

  // Light themes: clear zebra striping for dense enterprise grids
  const stripeMap: Partial<Record<EnterpriseThemeId, string>> = {
    material: '#F3EDF7',
    muiLight: '#F0F7FC',
    triton: '#EEF5FB',
    neptune: '#E8F2FA',
    crisp: '#F2F8F0',
    classic: '#EAF1FB',
    gray: '#EDEDED',
    fluent: '#EFF6FC',
    lightning: '#EAF5FE',
    fiori: '#EDF3F8',
    carbon: '#EDF5FF',
    slate: '#F1F5F9',
  }

  return {
    '--ac-north-bg': surface,
    '--ac-north-fg': on,
    '--ac-west-head-bg': bg,
    '--ac-west-head-fg': on,
    '--ac-title-accent': p,
    '--ac-border': 'rgba(0,0,0,0.12)',
    '--ac-shell-bg': bg,
    '--ag-header-background-color': '#F0F0F0',
    '--ag-background-color': surface,
    '--ag-foreground-color': on,
    '--ag-border-color': 'rgba(0,0,0,0.18)',
    '--ag-secondary-border-color': 'rgba(0,0,0,0.12)',
    '--ag-odd-row-background-color': stripeMap[id] || '#F5F8FA',
    '--ag-even-row-background-color': surface,
    '--ag-selected-row-background-color': hexToRgba(p, 0.14),
    '--ag-row-hover-color': hexToRgba(p, 0.07),
  }
}

function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace('#', '')
  if (h.length !== 6) return `rgba(25, 118, 210, ${alpha})`
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

export function isEnterpriseThemeId(v: unknown): v is EnterpriseThemeId {
  return typeof v === 'string' && THEME_CATALOG.some((t) => t.id === v)
}

export const DEFAULT_THEME_ID: EnterpriseThemeId = 'muiLight'
export const LS_THEME = 'na-ui-theme'
